<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="form-page">
    <div class="form-page-header">
      <div class="form-page-header__icon">
        <i class="pi pi-user-plus"></i>
      </div>
      <div class="form-page-header__text">
        <h1 class="form-page-header__title">إضافة حجز ثالثة إعدادي</h1>
        <p class="form-page-header__subtitle">تسجيل بيانات طالب جديد في الفصل الدراسي</p>
      </div>
    </div>

    <div class="container-fluid">
      <form @submit.prevent="sendForm()">

        <!-- Personal info -->
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-id-card"></i></div>
            <span class="form-card__title">البيانات الشخصية</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">اسم الطالب <span class="required">*</span></label>
                  <InputText id="studentName" v-model="student_name" placeholder="أدخل الاسم كاملاً" class="w-100" />
                  <span class="field-error" v-if="v$.student_name.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء ادخال اسم الطالب
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">رقم هاتف الطالب <span class="required">*</span></label>
                  <InputText
                    id="studentPhone"
                    v-model="student_phone"
                    inputmode="tel"
                    placeholder="01XXXXXXXXX"
                    dir="ltr"
                    class="w-100"
                    @keypress="onlyNumbers"
                  />
                  <span class="field-error" v-if="v$.student_phone.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء ادخال رقم هاتف الطالب
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">رقم هاتف ولي الأمر <span class="optional">اختياري</span></label>
                  <InputText
                    id="parentPhone"
                    v-model="parent_phone"
                    inputmode="tel"
                    placeholder="01XXXXXXXXX"
                    dir="ltr"
                    class="w-100"
                    @keypress="onlyNumbers"
                  />
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">الجنس <span class="required">*</span></label>
                  <Dropdown v-model="selected_gender" :options="genders" showClear optionLabel="name" placeholder="اختر الجنس" class="w-100" />
                  <span class="field-error" v-if="v$.selected_gender.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء اختيار الجنس
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">نظام التعليم <span class="optional">اختياري</span></label>
                  <Dropdown v-model="selected_type" :options="types" showClear optionLabel="name" placeholder="اختر نظام التعليم" class="w-100" />
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">العنوان <span class="optional">اختياري</span></label>
                  <InputText id="address" v-model="address" placeholder="المدينة، الحي..." class="w-100" />
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">درجة اللغة العربية <span class="optional">اختياري</span></label>
                  <InputText
                    id="mathDegree"
                    v-model="math_degree"
                    inputmode="numeric"
                    placeholder="الدرجة في الصف الثاني الإعدادي"
                    class="w-100"
                    @keypress="onlyNumbers"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Certificate upload -->
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-file-import"></i></div>
            <span class="form-card__title">صورة الشهادة</span>
            <span class="form-card__subtitle">شهادة الصف الثاني الإعدادي</span>
          </div>
          <div class="form-card__body">
            <label class="field-label">رفع الشهادة <span class="optional">اختياري</span></label>
            <div :class="['upload-zone', { 'upload-zone--has-file': imageEntry }]">
              <div class="upload-zone__icon">
                <i :class="`pi ${imageEntry ? 'pi-check' : 'pi-cloud-upload'}`"></i>
              </div>
              <p class="upload-zone__label">{{ imageEntry ? fileData.name : 'اضغط لاختيار صورة الشهادة' }}</p>
              <p class="upload-zone__hint" v-if="!imageEntry">PNG, JPG, GIF</p>
              <input
                type="file"
                id="certificateImage"
                @change="onFileChange"
                accept="image/png, image/gif, image/jpeg, image/jpg"
              />
            </div>
            <p class="mt-2" style="font-size: 0.8rem; color: var(--warning-color, #f59e0b);">
              <i class="pi pi-info-circle"></i>
              برجاء احضار الشهادة الأصلية في أول حصة لدخول الدرس
            </p>
          </div>
        </div>

        <!-- WhatsApp notice -->
        <div class="notice-box notice-box--warning">
          <i class="pi pi-whatsapp" style="font-size: 1.25rem;"></i>
          <div>
            <strong>هل واجهتك مشكلة في الحجز؟</strong>
            <p class="mb-0 mt-1" style="font-size: 0.875rem;">
              تواصل معنا على الواتساب:
              <a href="https://wa.me/+201111488849" target="_blank" style="font-weight: 700; margin-inline: 4px;">01111488849</a>
              أو
              <a href="https://wa.me/+201021631271" target="_blank" style="font-weight: 700; margin-inline: 4px;">01021631271</a>
            </p>
          </div>
        </div>

        <div class="form-actions">
          <button type="submit" class="btn-submit" :disabled="formSending">
            <i class="pi pi-spin pi-spinner-dotted" v-if="formSending"></i>
            <i class="pi pi-check" v-else></i>
            {{ formSending ? 'جاري الإرسال...' : 'تأكيد الحجز' }}
          </button>
        </div>

      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Swal from 'sweetalert2';
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import Dropdown from 'primevue/dropdown';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';

export default {
    name: "reservation-page",
    data() {
        return {
            v$: useVuelidate(),
            student_name: "",
            student_phone: null,
            parent_phone: null,
            address: "",
            math_degree: null,
            certificate_image: "",
            imageEntry: "",
            fileData: "",
            formSending: false,
            selected_gender: null,
            selected_type: null,
            genders: [
                { name: 'ولد', id: 1 },
                { name: 'بنت', id: 2 },
            ],
            types: [
                { name: 'عام', id: 1 },
                { name: 'أزهر', id: 2 },
            ],
            breadcrumbs: [
                { name: 'الرئيسية', path: '/' },
                { name: 'حجز ثالثة إعدادي', path: '/reservation' },
                { name: 'إضافة حجز ثالثة إعدادي', path: '/reservation/add' }
            ]
        }
    },
    components: {
        InputText,
        InputNumber,
        Dropdown,
        Breadcrumb
    },
    validations() {
        return {
            student_name: { required },
            student_phone: { required },
            // parent_phone: { required },
            selected_gender: { required },
            // selected_type: { required },
            // address: { required },
            // math_degree: { required },
            // fileData: { required },
        };
    },
    computed: {
        gradeStore() { return useGradeStore(); },
        gradeId() {
            const grade = this.gradeStore.allGrades.find(g => g.education_level_legacy == 3)
                || this.gradeStore.activeGrades.find(g => g.education_level_legacy == 3);
            return grade?.id ?? null;
        },
    },
    methods: {
        onFileChange(event) {
            this.fileData = event.target.files[0];
            const reader = new FileReader();
            reader.readAsDataURL(this.fileData);
            reader.onload = () => (this.imageEntry = reader.result);
        },
        onlyNumbers(event) {
            // يمنع أي مفتاح غير رقم عربي أو إنجليزي
            const char = String.fromCharCode(event.which);
            // إذا ليس رقم، امنع الإدخال
            if (!/[0-9٠-٩]/.test(char)) {
                event.preventDefault();
                return;
            }
            // إذا الطول الحالي 11 رقم، امنع الإدخال
            const value = event.target.value;
            // احسب عدد الأرقام فقط (عربي أو إنجليزي)
            const digitsCount = (value.match(/[0-9٠-٩]/g) || []).length;
            if (digitsCount >= 11) {
                event.preventDefault();
            }
        },
        toEnglishDigits(str) {
            if (!str) return str;
            return str.replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));
        },
        sendForm() {
            this.v$.$validate();
            if (!this.v$.$error) {
                const formData = new FormData();
                formData.append('student_name', this.student_name);
                formData.append('student_phone', this.toEnglishDigits(this.student_phone));
                if(this.parent_phone) {
                  formData.append('parent_phone', this.toEnglishDigits(this.parent_phone));
                }
                if(this.address) {
                  formData.append('address', this.address);
                }
                formData.append('gender', this.selected_gender.id);
                if(this.selected_type) {
                  formData.append('education_type', this.selected_type.id);
                }
                if(this.math_degree) {
                  formData.append('math_degree', this.math_degree);
                }
                if (this.gradeId) formData.append('academic_year_id', this.gradeId);

                if (this.fileData instanceof File) {
                    formData.append("certificate_image", this.fileData);
                }
                this.formSending = true;

                axios
                    .post('/register_dashboard', formData)
                    .then((response) => {
                        this.formSending = false;
                        this.resetForm();
                        Swal.fire({
                            position: "center",
                            icon: "success",
                            title: "رقم الهاتف الخاص بك  " + response.data.data.phone,
                            text: "برجاء تذكر رقم الهاتف لمعرفة حالة حجزك وزيارة الموقع مره اخرى في خلال 72 ساعة من اجل معرفة حالة طلبك"
                        });
                    })
                    .catch((error) => {
                        this.formSending = false;
                        Swal.fire({
                            position: "center",
                            icon: "error",
                            title: error.response.data.message,
                        });
                    });
            }
        },
        resetForm() {
            this.v$.$reset();
            this.student_name = "";
            this.student_phone = null;
            this.parent_phone = null;
            this.address = "";
            this.math_degree = null;
            this.certificate_image = "";
            this.imageEntry = "";
            this.fileData = "";
            this.selected_gender = null;
            this.selected_type = null;
        }
    },
    async mounted() {
        if (!this.gradeStore.allGrades.length) await this.gradeStore.fetchAllGrades();
        if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
    },
}
</script>
